import { Button, Dropdown, Modal, Typography } from '@design-system';
import React from 'react';
import { IoMdClose } from 'react-icons/io';

import { GetTimesheetsTasksResponse, GetUsersResponse, TaskState } from '@services';
import { usePointageTask } from './usePointageTask';

import './PointageTask.scss';

export type TaskData = {
    operator_id: string | number;
    worksite_id: string | number;
    hours: string | number;
    type_id: string | number;
    summedHours?: number;
    id?: string | number;
    isNew?: boolean;
    state_id?: TaskState;
    reason?: string;
};

export type PointageTaskProps = {
    taskId: string | number;
    initialTask: TaskData;
    onTaskChange: (taskId: string | number, taskData: TaskData) => void;
    onTaskDelete: (taskId: string | number) => void;
    tasksTypes: GetTimesheetsTasksResponse;
    operators: GetUsersResponse['operators'];
    taskState?: TaskState;
    index: number;
    summedHours?: number;
};

export const PointageTask: React.FC<PointageTaskProps> = ({
    taskId,
    initialTask,
    onTaskChange,
    onTaskDelete,
    tasksTypes,
    operators,
    taskState,
    index,
    summedHours
}) => {
    const { hoursOptions, taskData, operatorsOptions, worksitesOptions, handleFieldChange, showModal, setShowModal } = usePointageTask({
        initialTask,
        operators,
        onTaskChange,
        taskId,
        summedHours
    });

    const isTaskValidated = taskState === TaskState.VALIDATED;
    const isTaskRefused = taskState === TaskState.REFUSED;

    return (
        <>
            <div className={`pointage-task ${isTaskValidated ? 'validated' : ''} ${isTaskRefused ? 'refused' : ''}`}>
                <div className="task-header">
                    <Typography intrinsic="h3">Tâche n°{index + 1}</Typography>
                    {!isTaskValidated && (
                        <Button
                            color="grey"
                            variant="tertiary"
                            icon={<IoMdClose />}
                            iconPosition="right"
                            iconSize={20}
                            onClick={() => {
                                setShowModal(true);
                            }}
                        >
                            <Typography intrinsic="h4">Supprimer</Typography>
                        </Button>
                    )}
                </div>

                <Dropdown
                    options={operatorsOptions}
                    placeholder="Responsable"
                    onOptionChange={(option) => handleFieldChange('operator_id', option.id)}
                    value={operatorsOptions.find((operator) => operator.id === taskData.operator_id)}
                    disabled={isTaskValidated}
                />

                <Dropdown
                    options={worksitesOptions}
                    placeholder="Chantier"
                    onOptionChange={(option) => handleFieldChange('worksite_id', option.id)}
                    value={
                        taskData.worksite_id && worksitesOptions.length > 0
                            ? worksitesOptions.find((worksite) => worksite.id === taskData.worksite_id) || null
                            : null
                    }
                    disabled={isTaskValidated}
                    isSearchable={true}
                />

                <Dropdown
                    options={hoursOptions}
                    placeholder="Nombre d'heures"
                    onOptionChange={(option) => handleFieldChange('hours', option.id)}
                    value={hoursOptions.find((hours) => hours.id === taskData.hours)}
                    disabled={isTaskValidated}
                />

                <Dropdown
                    options={tasksTypes}
                    placeholder="Type de tâche"
                    onOptionChange={(option) => handleFieldChange('type_id', option.id)}
                    value={tasksTypes.find((type) => type.id === taskData.type_id)}
                    disabled={isTaskValidated}
                />

                {isTaskRefused && initialTask.reason && (
                    <div className="task-refused-reason">
                        <Typography intrinsic="p" color="marine">
                            Raison du refus : {initialTask.reason}
                        </Typography>
                    </div>
                )}
            </div>
            {!isTaskValidated && (
                <Modal
                    title="Confirmation"
                    open={showModal}
                    setClose={() => {
                        setShowModal(false);
                    }}
                    footer={
                        <div className="modal-footer">
                            <Button
                                color="grey"
                                variant="tertiary"
                                icon={<IoMdClose />}
                                iconPosition="right"
                                iconSize={20}
                                onClick={() => {
                                    onTaskDelete(taskId);
                                    setShowModal(false);
                                }}
                            >
                                <Typography intrinsic="h4">Supprimer</Typography>
                            </Button>
                            <Button
                                color="green"
                                variant="secondary"
                                size="small"
                                onClick={() => {
                                    setShowModal(false);
                                }}
                            >
                                <Typography intrinsic="h4">Annuler</Typography>
                            </Button>
                        </div>
                    }
                >
                    <Typography intrinsic="p" variant="secondary">
                        Êtes-vous sûr de vouloir supprimer cette tâche ?
                    </Typography>
                </Modal>
            )}
        </>
    );
};
